import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import PropTypes from 'prop-types';

import { downloadScientificProfileFile } from 'apis/projectProposals.api';
import DownloadScientificProfile from 'components/DownloadScientificProfile';
import WarningScientificProfileUrl from 'components/WarningScientificProfileUrl';
import { getAcadamicLevelShortHand, toNonAccentVietnamese } from 'utils/func';
import { toast } from 'layout';
import { TOAST } from 'constant';
import DiffColumn from '../DiffColumn';

function ContactCompareTable({ data1, data2, diffObj, isLoading }) {
  const { t } = useTranslation();

  const { mutate: mutateDownloadScientificProfileFile } = useMutation(
    downloadScientificProfileFile
  );
  // #region Event
  const handleDownloadScientificProfileFile = (
    contactId,
    contactName = '',
    academicRank = '',
    academicDegree = ''
  ) => {
    if (contactId && contactName) {
      mutateDownloadScientificProfileFile(contactId, {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = `${getAcadamicLevelShortHand(
              academicRank,
              academicDegree,
              t,
              true
            )}${toNonAccentVietnamese(contactName)}.pdf`;

            link.click();
          }
        },
        onError: () => {
          toast(
            TOAST.ERROR,
            t('projectProposal.general.researchHumanResources.downloadScientificProfileErrorTitle')
          );
        },
      });
    }
  };
  // #endregion Event

  // #region Render
  const formatIndex = (value, { rowIndex }) => rowIndex + 1;

  const formatFullname = (value) => (
    <>
      {value?.diffStringFullname}
      {value?.contact?.user?.scientificProfileUrl ? (
        <DownloadScientificProfile
          contactId={value?.id || value?.contact?.id || value?.contactId}
          contactName={value?.fullname}
          academicRank={value?.academicRank}
          academicDegree={value?.academicDegree}
          handleDownloadFile={handleDownloadScientificProfileFile}
        />
      ) : (
        <WarningScientificProfileUrl />
      )}
    </>
  );

  // #endregion Render

  return (
    <tr className="datatable">
      <td className="py-2">
        <DataTable
          value={data1}
          showGridlines
          dataKey="contact.id"
          loading={isLoading}
          responsiveLayout="scroll"
          scrollable
        >
          <Column
            header={t('formLayout.numericalOrder')}
            body={formatIndex}
            className="text-center"
          />
          <Column
            header={`${t(
              'projectProposal.general.researchHumanResources.fields.academicRank'
            )}, ${t('projectProposal.general.researchHumanResources.fields.academicDegree')}, ${t(
              'projectProposal.general.researchHumanResources.fields.name'
            )}`}
            body={formatFullname}
            style={{ minWidth: '10rem' }}
          />
          <Column
            field="diffStringOrganization"
            header={t('projectProposal.general.researchHumanResources.fields.organization')}
            style={{ minWidth: '10rem' }}
          />
        </DataTable>
      </td>
      <td className="py-2">
        <DataTable
          value={data2}
          showGridlines
          dataKey="contact.id"
          loading={false}
          responsiveLayout="scroll"
          scrollable
        >
          <Column
            header={t('formLayout.numericalOrder')}
            body={formatIndex}
            className="text-center"
          />
          <Column
            header={`${t(
              'projectProposal.general.researchHumanResources.fields.academicRank'
            )}, ${t('projectProposal.general.researchHumanResources.fields.academicDegree')}, ${t(
              'projectProposal.general.researchHumanResources.fields.name'
            )}`}
            body={formatFullname}
            style={{ minWidth: '10rem' }}
          />
          <Column
            field="diffStringOrganization"
            header={t('projectProposal.general.researchHumanResources.fields.organization')}
            style={{ minWidth: '10rem' }}
          />
        </DataTable>
      </td>
      <DiffColumn
        isDifferent={
          !!Object.values(diffObj)?.some(
            (e) =>
              e === true ||
              Object.entries(e)?.some(
                ([key, val]) =>
                  key !== 'projectProposalId' &&
                  key !== 'createdAt' &&
                  key !== 'updatedAt' &&
                  val === true
              )
          )
        }
      />
    </tr>
  );
}

export default ContactCompareTable;

ContactCompareTable.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data1: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  data2: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  diffObj: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
};
